<template>
  <div class="ledger-page pb-5 pb-md-2">
    <b-card class="rounded-lg">
      <div class="d-flex justify-content-between align-items-start flex-wrap mb-1">
        <h4 class="mb-50">
          {{ $t('ledger_assets_register') }}
        </h4>
        <div class="d-flex flex-wrap">
          <b-button
            size="sm"
            variant="outline-primary"
            class="mr-50 mb-50"
            :to="{ name: 'ledger-depreciation' }"
          >
            {{ $t('ledger_depr_title') }}
          </b-button>
          <b-button
            size="sm"
            variant="outline-primary"
            class="mr-50 mb-50"
            :to="{ name: 'ledger-assets-statement' }"
          >
            {{ $t('ledger_assets_statement') }}
          </b-button>
          <b-button
            v-if="canCreate"
            size="sm"
            variant="primary"
            class="mb-50"
            :to="{ name: 'ledger-assets-new' }"
          >
            {{ $t('ledger_assets_new') }}
          </b-button>
        </div>
      </div>

      <b-row class="mb-1">
        <b-col
          cols="12"
          md="4"
        >
          <b-form-input
            v-model="keyword"
            :placeholder="$t('ledger_search')"
            @keyup.enter="loadAssets"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
        >
          <b-form-select
            v-model="category"
            :options="categoryOptions"
            @change="loadAssets"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
        >
          <b-form-select
            v-model="status"
            :options="statusOptions"
            @change="loadAssets"
          />
        </b-col>
      </b-row>

      <div
        v-if="loading"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
      </div>
      <div
        v-else-if="!rows.length"
        class="text-center text-muted py-4"
      >
        {{ $t('ledger_assets_empty') }}
      </div>
      <div v-else>
        <div class="d-none d-md-block">
          <b-table
            :items="rows"
            :fields="fields"
            small
            hover
            responsive
            class="ledger-dense-table"
          >
            <template #cell(asset_code)="data">
              <b-link :to="showRoute(data.item)">
                {{ data.item.asset_code }}
              </b-link>
            </template>
            <template #cell(name)="data">
              {{ displayName(data.item) }}
            </template>
            <template #cell(category)="data">
              <b-badge variant="light-primary">
                {{ enumLabel(data.item.category, $i18n.locale) }}
              </b-badge>
            </template>
            <template #cell(cost)="data">
              <span class="ledger-num">{{ money(data.item.cost) }}</span>
            </template>
            <template #cell(accumulated_depreciation)="data">
              <span class="ledger-num">{{ money(data.item.accumulated_depreciation) }}</span>
            </template>
            <template #cell(net_book_value)="data">
              <span class="ledger-num">{{ money(data.item.net_book_value) }}</span>
            </template>
            <template #cell(status)="data">
              <b-badge :variant="statusBadgeVariant(enumValue(data.item.status))">
                {{ enumLabel(data.item.status, $i18n.locale) }}
              </b-badge>
            </template>
          </b-table>
        </div>
        <div class="d-md-none">
          <article
            v-for="item in rows"
            :key="item.id"
            class="ledger-asset-card"
            @click="$router.push(showRoute(item))"
          >
            <div class="d-flex justify-content-between">
              <strong>{{ item.asset_code }}</strong>
              <b-badge :variant="statusBadgeVariant(enumValue(item.status))">
                {{ enumLabel(item.status, $i18n.locale) }}
              </b-badge>
            </div>
            <div>{{ displayName(item) }}</div>
            <div class="small text-muted">
              {{ enumLabel(item.category, $i18n.locale) }}
            </div>
            <div class="ledger-num mt-50">
              {{ $t('ledger_assets_nbv') }} {{ money(item.net_book_value) }}
            </div>
          </article>
        </div>
      </div>
    </b-card>

    <div
      v-if="rows.length"
      class="ledger-asset-sticky d-md-none"
    >
      <span>{{ $t('ledger_assets_nbv') }}</span>
      <strong class="ledger-num">{{ money(nbvSum) }}</strong>
    </div>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BFormSelect, BButton, BTable, BBadge, BLink, BSpinner,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getFixedAssets } from '@/api/accounting/fixedAssets'
import {
  listFrom, enumValue, enumLabel, formatLedgerMoney, roundMoney, statusBadgeVariant, apiErrorMessage,
} from '@/utils/ledger'

const CATEGORIES = ['land', 'buildings', 'survey_equipment', 'computers', 'furniture', 'vehicles']

export default {
  name: 'FixedAssetsList',
  components: {
    BCard, BRow, BCol, BFormInput, BFormSelect, BButton, BTable, BBadge, BLink, BSpinner,
  },
  data() {
    return {
      loading: false,
      keyword: '',
      category: '',
      status: '',
      rows: [],
    }
  },
  computed: {
    canCreate() {
      return this.hasPermission('create fixed_assets')
    },
    categoryOptions() {
      return [
        { value: '', text: this.$t('ledger_assets_all_categories') },
        ...CATEGORIES.map(value => ({ value, text: this.$t(`ledger_assets_cat_${value}`) })),
      ]
    },
    statusOptions() {
      return [
        { value: '', text: this.$t('ledger_assets_all_statuses') },
        { value: 'active', text: this.$t('ledger_assets_status_active') },
        { value: 'fully_depreciated', text: this.$t('ledger_assets_status_full') },
      ]
    },
    fields() {
      return [
        { key: 'asset_code', label: this.$t('ledger_assets_code') },
        { key: 'name', label: this.$t('ledger_name') },
        { key: 'category', label: this.$t('ledger_assets_category') },
        { key: 'cost', label: this.$t('ledger_assets_cost') },
        { key: 'accumulated_depreciation', label: this.$t('ledger_assets_accumulated') },
        { key: 'net_book_value', label: this.$t('ledger_assets_nbv') },
        { key: 'status', label: this.$t('ledger_status') },
      ]
    },
    nbvSum() {
      return this.rows.reduce((sum, row) => roundMoney(sum + (Number(row.net_book_value) || 0)), 0)
    },
  },
  mounted() {
    this.loadAssets()
  },
  methods: {
    enumValue,
    enumLabel,
    statusBadgeVariant,
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    displayName(asset) {
      if (!asset) return ''
      if (this.$i18n.locale === 'en') return asset.name || asset.name_ar || ''
      return asset.name_ar || asset.name || ''
    },
    showRoute(asset) {
      return { name: 'ledger-assets-view', params: { id: asset.id } }
    },
    async loadAssets() {
      this.loading = true
      try {
        const params = {}
        const keyword = String(this.keyword || '').trim()
        if (keyword) params.keyword = keyword
        if (this.category) params.category = this.category
        if (this.status) params.status = this.status
        const res = await getFixedAssets(params)
        this.rows = listFrom(res)
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: apiErrorMessage(error, this.$t('ledger_error')),
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.ledger-num {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.ledger-asset-card {
  border-bottom: 1px solid #eee;
  padding: 0.85rem 0.15rem;
  cursor: pointer;
}
.ledger-asset-sticky {
  position: fixed;
  bottom: 0;
  inset-inline: 0;
  z-index: 20;
  display: flex;
  justify-content: space-between;
  background: #fff;
  border-top: 1px solid #e6e6e6;
  padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom));
}
</style>
